import { c as _c } from "react/compiler-runtime";
import { isSameDay, sub } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import NumberFormat from 'react-number-format';
import { usePopper } from 'react-popper';
import styled, { css, keyframes } from 'styled-components';
import { formatDate } from '../../../utils/date';
import { useDatePreferences } from '../../hooks/useDatePreferences';
import { StyledCard, StyledCardSection } from '../base/Card';
import Icon from '../base/Icon';
import Text from '../base/Text';
import { Div } from '../helpers/StyledUtils';
export interface PreviewBucket {
  range: [Date, Date];
  values: Record<string, number>;
}
export interface PreviewMetricEntry {
  time: number;
  count: number;
  id: string;
  dimension: string;
}
export const GROUP_METRICS_BY_2H = {
  samples: 12,
  samples_length: 2
};
export const GROUP_METRICS_BY_4H = {
  samples: 6,
  samples_length: 4
};
export function buildPreviewBuckets(ids: string[], entries: PreviewMetricEntry[], sample_config: {
  samples: number;
  samples_length: number;
}, now: Date): Record<string, PreviewBucket[]> {
  return ids.reduce((obj, id) => {
    obj[id] = Array.from(Array(sample_config.samples).keys()).reduce((arr: PreviewBucket[], i) => {
      const step_start = sub(now, {
        hours: i * sample_config.samples_length
      });
      const step_end = sub(step_start, {
        hours: sample_config.samples_length
      });
      const matching = entries.filter(entry => step_end.getTime() < entry.time && step_start.getTime() >= entry.time && entry.id === id);
      const values = matching.reduce((rollup, entry) => {
        if (!rollup[entry.dimension]) {
          rollup[entry.dimension] = 0;
        }
        rollup[entry.dimension] += entry.count;
        return rollup;
      }, {} as Record<string, number>);
      arr.push({
        range: [step_start, step_end],
        values
      });
      return arr;
    }, []).reverse();
    return obj;
  }, {} as Record<string, PreviewBucket[]>);
}
const StyledChartContainer = styled.div<{
  size: 'small' | 'medium';
}>(({
  theme,
  size
}) => css`
    height: ${size === 'small' ? '19px' : '36px'};
    display: flex;
    align-items: flex-end;
    min-width: 100%;
    margin-bottom: ${theme.spacing(0)};
    border-bottom: 1px solid ${theme.colors.outline.neutral};
  `);
const StyledChartEntry = styled.div<{
  low_opacity?: boolean;
  hovered?: boolean;
  within_range?: boolean;
}>(({
  low_opacity,
  hovered,
  theme
}) => css`
    display: flex;
    flex-direction: column-reverse;
    position: relative;
    z-index: 1;
    padding: 0 1px;
    padding-bottom: 1px;
    height: 100%;
    flex-grow: 1;

    ${low_opacity && !hovered && css`
      ${StyledChartValue} {
        opacity: ${low_opacity ? 0.5 : 1};
        transition: opacity 0.15s ease-in;
      }
    `}
    ${hovered && css`
      background-color: ${theme.colors.surface.base.hover.neutral};
      z-index: 10;
    `}
  `);
const pulse_keyframe = keyframes`
  0% {
    opacity: 0.8;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 0.8;
  }
`;
const StyledChartValue = styled.div<{
  theme_color?: string;
  $loading?: boolean;
  max_value: number;
  value: number;
}>(({
  theme,
  max_value,
  value,
  $loading,
  theme_color
}) => css`
    height: ${(value / max_value * 100).toFixed(1)}%;
    min-height: 2px;
    background-color: ${value === 0 || !theme_color ? theme.colors.surface.base.variant_surface : theme.colors.surface.base[theme_color] as any};
    border-radius: 1px;
    width: 100%;
    margin: 0.5px 0px;
    align-self: end;

    ${$loading && css`
      background-color: ${theme.colors.surface.base.variant_surface};
      animation: ${pulse_keyframe} 1.5s ease-in-out 0.5s infinite;
    `}
  `);
const StyledChartTooltipWrapper = styled.div`
  width: fit-content;
  z-index: 2000;
`;
const StyledChartTooltip = styled(StyledCard)(({
  theme
}) => css`
    box-shadow: ${theme.elevation[2]};
    margin-top: 8px;
  `);
const granularity_formating = {
  second: 'MMM d, h:mm:ss a',
  minute: 'MMM d, h:mm a',
  hour: 'MMM d, h:mm a',
  day: 'MMM d, h:mm a'
};
const getTooltipTimeLabel = (start: Date, end: Date, resolution: keyof typeof granularity_formating, timezone: string) => {
  const format_string = granularity_formating[resolution];
  const start_label = formatDate(start, format_string, timezone);
  const end_label = formatDate(end, isSameDay(utcToZonedTime(start, timezone), utcToZonedTime(end, timezone)) ? format_string.replace('MMM d,', '') : format_string, timezone);
  return <Div flex={{
    justify: 'space-between',
    align: 'center',
    gap: 1
  }}>
      <Text semi>{start_label}</Text>
      <Icon icon="arrow_forward" />
      <Text semi>{end_label}</Text>
    </Div>;
};
const PreviewHistogram: React.FC<{
  values: PreviewBucket[];
  resolution: keyof typeof granularity_formating;
  dimensions_config: {
    [key: string]: {
      color: string;
      label: string;
    };
  };
  show_tooltip?: boolean;
  size?: 'small' | 'medium';
}> = t0 => {
  const $ = _c(36);
  const {
    values,
    resolution,
    show_tooltip,
    dimensions_config,
    size
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = document.querySelector("#main");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const element = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      placement: "bottom",
      modifiers: [{
        name: "preventOverflow",
        options: {
          boundary: element as any,
          mainAxis: true,
          altAxis: false,
          padding: 8
        }
      }]
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, t2);
  let t3;
  if ($[2] !== size) {
    t3 = Array.from(Array(size === "small" ? 6 : 14).keys()).map(_temp);
    $[2] = size;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const placeholder_data = t3;
  const [hovered_date, setHoveredDate] = useState(null);
  let t4;
  if ($[4] !== values) {
    t4 = values && Math.max(...values.map(_temp3));
    $[4] = values;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const max_value = t4;
  let t5;
  if ($[6] !== dimensions_config) {
    t5 = Object.keys(dimensions_config);
    $[6] = dimensions_config;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const dimention_keys = t5;
  const t6 = size || "medium";
  let t7;
  if ($[8] !== placeholder_data || $[9] !== values) {
    t7 = !values || values.length === 0 && placeholder_data.map(_temp4);
    $[8] = placeholder_data;
    $[9] = values;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== max_value) {
    t8 = !max_value && <Text neutral={true} center={true} mono={true} size="xs" w={100}>NO DATA</Text>;
    $[11] = max_value;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== dimensions_config || $[14] !== dimention_keys || $[15] !== hovered_date || $[16] !== max_value || $[17] !== show_tooltip || $[18] !== values) {
    t9 = max_value ? values?.map(t10 => {
      const {
        range: t11,
        values: object
      } = t10;
      const [start_date, end_date] = t11;
      const hovered = hovered_date ? hovered_date.start_date.getTime() === start_date.getTime() : undefined;
      return <StyledChartEntry hovered={hovered || undefined} key={start_date.toISOString()} low_opacity={hovered_date && !hovered || undefined} onMouseEnter={() => show_tooltip && setHoveredDate({
        start_date,
        end_date
      })} onMouseLeave={() => show_tooltip && setHoveredDate(null)}>{hovered && <div ref={setReferenceElement} />}{Object.entries(object).sort((t12, t13) => {
          const [dimention_a] = t12;
          const [dimention_b] = t13;
          return dimention_keys.indexOf(dimention_a) - dimention_keys.indexOf(dimention_b);
        }).filter(t14 => {
          const [dimention, count] = t14;
          return count > 0 && dimensions_config[dimention];
        }).map(t15 => {
          const [dimention_0, count_0] = t15;
          return count_0 ? <StyledChartValue key={dimention_0} theme_color={dimensions_config[dimention_0].color} max_value={max_value} value={count_0} /> : null;
        })}</StyledChartEntry>;
    }) : null;
    $[13] = dimensions_config;
    $[14] = dimention_keys;
    $[15] = hovered_date;
    $[16] = max_value;
    $[17] = show_tooltip;
    $[18] = values;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== attributes || $[21] !== dimensions_config || $[22] !== dimention_keys || $[23] !== hovered_date || $[24] !== resolution || $[25] !== show_tooltip || $[26] !== styles || $[27] !== timezone || $[28] !== values) {
    t10 = show_tooltip && hovered_date && <StyledChartTooltipWrapper ref={setPopperElement} style={styles.popper} {...attributes.popper}><StyledChartTooltip overflow_hidden={true}><StyledCardSection p={{
          x: 3,
          y: 2
        }}><Text bold={true} text_wrap={false} as="span">{getTooltipTimeLabel(hovered_date.end_date, hovered_date.start_date, resolution, timezone)}</Text></StyledCardSection><StyledCardSection p={{
          x: 3,
          y: 2
        }}>{Object.entries(values.find(t11 => {
            const {
              range: t12
            } = t11;
            const [start_date_0] = t12;
            return start_date_0.toISOString() === hovered_date.start_date.toISOString();
          })?.values || {}).filter(t13 => {
            const [dimention_1] = t13;
            return dimensions_config[dimention_1];
          }).sort((t14, t15) => {
            const [dimention_a_0] = t14;
            const [dimention_b_0] = t15;
            return dimention_keys.indexOf(dimention_a_0) - dimention_keys.indexOf(dimention_b_0);
          }).map((t16, i_0) => {
            const [dimention_2, count_1] = t16;
            return <Div key={dimention_2} m={{
              t: i_0 === 0 ? 0 : 1
            }} flex={{
              justify: "space-between"
            }}><Text semi={true}>{dimensions_config[dimention_2].label}</Text><Text semi={true} m={{
                l: 4
              }}><NumberFormat renderText={_temp5} displayType="text" value={count_1} thousandSeparator="," /></Text></Div>;
          })}{Object.entries(values.find(t17 => {
            const {
              range: t18
            } = t17;
            const [start_date_1] = t18;
            return start_date_1.toISOString() === hovered_date.start_date.toISOString();
          })?.values || {}).length === 0 && <Text muted={true}>No events</Text>}</StyledCardSection></StyledChartTooltip></StyledChartTooltipWrapper>;
    $[20] = attributes;
    $[21] = dimensions_config;
    $[22] = dimention_keys;
    $[23] = hovered_date;
    $[24] = resolution;
    $[25] = show_tooltip;
    $[26] = styles;
    $[27] = timezone;
    $[28] = values;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t11 = <StyledChartContainer size={t6}>{t7}{t8}{t9}{t10}</StyledChartContainer>;
    $[30] = t10;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
export default PreviewHistogram;
function _temp() {
  return Math.random() * 100;
}
function _temp2(a, b) {
  return a + b;
}
function _temp3(t0) {
  const {
    values: v
  } = t0;
  return Object.values(v).reduce(_temp2, 0);
}
function _temp4(v_0, i) {
  return <StyledChartEntry key={i}><StyledChartValue $loading={true} max_value={100} value={v_0} /></StyledChartEntry>;
}
function _temp5(v_1) {
  return v_1;
}